import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link,
  useNavigate
} from 'react-router-dom';

import axios from 'axios';

import {
  Building2,
  MapPin,
  Plus,
  Users,
  ArrowRight,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';

import {
  MapContainer,
  TileLayer,
  Polygon,
  Tooltip
} from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ModonLogo from '../components/ModonLogo';
import './AdminStations.modon.css';


const API = '/api';


export default function AdminStations() {

  const nav = useNavigate();

  const [stations, setStations] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const [showForm, setShowForm] =
    useState(false);

  const [form, setForm] = useState({
    name: '',
    code: '',
    city: '',
    description: '',
    managerName: '',
    managerEmail: '',
    managerPhone: '',
    managerPassword: '',
  });


  const set = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.value
    }));
  };


  const loadStations = async () => {
    try {
      setLoading(true);
      setError('');

      const r = await axios.get(
        `${API}/admin/stations`,
        {
          withCredentials: true
        }
      );

      setStations(
        Array.isArray(r.data)
          ? r.data
          : []
      );

    } catch (e) {

      setError(
        e?.response?.data?.detail ||
        'تعذر تحميل المحطات'
      );

    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadStations();
  }, []);


  const createStation = async (e) => {

    e.preventDefault();

    setError('');

    if (
      !form.name.trim() ||
      !form.code.trim() ||
      !form.city.trim()
    ) {
      setError(
        'أدخل اسم المحطة والكود والمدينة'
      );
      return;
    }

    if (
      !form.managerName.trim() ||
      !form.managerEmail.trim() ||
      !form.managerPassword
    ) {
      setError(
        'أدخل بيانات مدير المحطة'
      );
      return;
    }

    try {

      setSaving(true);

      // 1) Create station
      const rs = await axios.post(
        `${API}/admin/stations`,
        {
          name: form.name.trim(),
          code: form.code.trim(),
          city: form.city.trim(),
          description:
            form.description.trim(),
          boundary: [],
          is_active: true,
        },
        {
          withCredentials: true
        }
      );

      const station = rs.data;

      // 2) Create manager
      try {

        await axios.post(
          `${API}/admin/stations/${station.id}/manager`,
          {
            name:
              form.managerName.trim(),

            email:
              form.managerEmail.trim(),

            phone:
              form.managerPhone.trim(),

            password:
              form.managerPassword,
          },
          {
            withCredentials: true
          }
        );

      } catch (managerError) {

        setError(
          'تم إنشاء المحطة، ولكن تعذر إنشاء مدير المحطة: ' +
          (
            managerError?.response
              ?.data?.detail ||
            'خطأ غير معروف'
          )
        );

        await loadStations();
        return;
      }

      setForm({
        name: '',
        code: '',
        city: '',
        description: '',
        managerName: '',
        managerEmail: '',
        managerPhone: '',
        managerPassword: '',
      });

      setShowForm(false);

      await loadStations();

    } catch (e) {

      setError(
        e?.response?.data?.detail ||
        'تعذر إنشاء المحطة'
      );

    } finally {
      setSaving(false);
    }
  };


  const polygons = useMemo(() => {

    return stations
      .filter(
        (s) =>
          Array.isArray(s.boundary) &&
          s.boundary.length >= 3
      )
      .map((station) => ({
        ...station,

        positions:
          station.boundary.map(
            ([lng, lat]) => [
              lat,
              lng
            ]
          ),
      }));

  }, [stations]);


  return (
    <div
      dir="rtl"
      className="admin-stations-modon-shell"
    >

      <header className="admin-stations-modon-header">

        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">

          <div className="flex items-center gap-3">

            <ModonLogo size={44} />

            <div>
              <h1 className="font-extrabold text-slate-900">
                إدارة المحطات
              </h1>

              <p className="text-xs text-slate-500">
                إنشاء المحطات وتعيين مديريها
              </p>
            </div>

          </div>


          <div className="flex items-center gap-2">

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                nav('/admin')
              }
            >
              <ArrowRight size={15}/>
              لوحة مدير النظام
            </Button>

            <Button
              size="sm"
              className="bg-[#1a4d80] hover:bg-[#143d68]"
              onClick={() =>
                setShowForm(
                  (v) => !v
                )
              }
            >
              <Plus size={15}/>
              إنشاء محطة
            </Button>

          </div>

        </div>

      </header>


      <main className="admin-stations-modon-main max-w-7xl mx-auto p-4 md:p-6 space-y-5">

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {typeof error === 'string'
              ? error
              : JSON.stringify(error)}
          </div>
        )}


        {showForm && (

          <Card className="p-5">

            <div className="mb-4">

              <h2 className="font-bold text-[#1a4d80]">
                إنشاء محطة جديدة
              </h2>

              <p className="text-xs text-slate-500 mt-1">
                يتم إنشاء المحطة أولًا، ثم يقوم مدير المحطة برسم حدود GPS من حسابه.
              </p>

            </div>


            <form
              onSubmit={createStation}
              className="space-y-4"
            >

              <div className="grid md:grid-cols-3 gap-3">

                <div>
                  <label className="text-xs font-bold">
                    اسم المحطة
                  </label>
                  <Input
                    value={form.name}
                    onChange={set('name')}
                    placeholder="مثال: محطة إطفاء مدن جدة الثانية"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold">
                    كود المحطة
                  </label>
                  <Input
                    value={form.code}
                    onChange={set('code')}
                    placeholder="JED-02"
                    dir="ltr"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold">
                    المدينة
                  </label>
                  <Input
                    value={form.city}
                    onChange={set('city')}
                    placeholder="جدة"
                  />
                </div>

              </div>


              <div>
                <label className="text-xs font-bold">
                  وصف المحطة
                </label>

                <Input
                  value={form.description}
                  onChange={set('description')}
                  placeholder="وصف اختياري"
                />
              </div>


              <div className="border-t pt-4">

                <div className="flex items-center gap-2 mb-3">

                  <ShieldCheck
                    size={17}
                    className="text-[#1a4d80]"
                  />

                  <h3 className="font-bold">
                    مدير المحطة
                  </h3>

                </div>


                <div className="grid md:grid-cols-2 gap-3">

                  <div>
                    <label className="text-xs font-bold">
                      اسم المدير
                    </label>
                    <Input
                      value={form.managerName}
                      onChange={set('managerName')}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold">
                      البريد الإلكتروني
                    </label>
                    <Input
                      value={form.managerEmail}
                      onChange={set('managerEmail')}
                      type="email"
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold">
                      رقم الجوال
                    </label>
                    <Input
                      value={form.managerPhone}
                      onChange={set('managerPhone')}
                      dir="ltr"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold">
                      كلمة المرور المؤقتة
                    </label>
                    <Input
                      value={form.managerPassword}
                      onChange={set('managerPassword')}
                      type="password"
                      dir="ltr"
                    />
                  </div>

                </div>

              </div>


              <div className="flex gap-2">

                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#1a4d80]"
                >
                  {saving
                    ? 'جارٍ الإنشاء...'
                    : 'إنشاء المحطة والمدير'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    setShowForm(false)
                  }
                >
                  إلغاء
                </Button>

              </div>

            </form>

          </Card>
        )}


        <div className="grid md:grid-cols-4 gap-3">

          <Card className="p-4">
            <div className="text-xs text-slate-500">
              إجمالي المحطات
            </div>

            <div className="text-2xl font-extrabold text-[#1a4d80]">
              {stations.length}
            </div>
          </Card>


          <Card className="p-4">
            <div className="text-xs text-slate-500">
              المحطات المحددة GPS
            </div>

            <div className="text-2xl font-extrabold text-emerald-600">
              {
                polygons.length
              }
            </div>
          </Card>


          <Card className="p-4">
            <div className="text-xs text-slate-500">
              المصانع المرتبطة
            </div>

            <div className="text-2xl font-extrabold">
              {
                stations.reduce(
                  (a, s) =>
                    a +
                    Number(
                      s.factory_count || 0
                    ),
                  0
                )
              }
            </div>
          </Card>


          <Card className="p-4">
            <div className="text-xs text-slate-500">
              مدراء المحطات
            </div>

            <div className="text-2xl font-extrabold">
              {
                stations.reduce(
                  (a, s) =>
                    a +
                    Number(
                      s.manager_count || 0
                    ),
                  0
                )
              }
            </div>
          </Card>

        </div>


        <Card className="overflow-hidden">

          <div className="p-4 border-b flex items-center justify-between">

            <div>
              <div className="font-bold text-[#1a4d80]">
                خريطة جميع المحطات
              </div>

              <div className="text-xs text-slate-500">
                عرض إشرافي فقط لمدير النظام
              </div>
            </div>

            <Button
              size="sm"
              variant="outline"
              onClick={loadStations}
            >
              <RefreshCw size={14}/>
              تحديث
            </Button>

          </div>


          <div className="h-[420px]">

            <MapContainer
              center={[21.5433, 39.1728]}
              zoom={10}
              className="h-full w-full"
            >

              <TileLayer
                attribution="&copy; OpenStreetMap"
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              {polygons.map(
                (station) => (

                  <Polygon
                    key={station.id}
                    positions={
                      station.positions
                    }
                    pathOptions={{
                      weight: 3,
                      fillOpacity: 0.12,
                    }}
                  >

                    <Tooltip>
                      <div dir="rtl">
                        <b>
                          {station.name}
                        </b>
                        <div>
                          {station.code}
                        </div>
                        <div>
                          المصانع:
                          {' '}
                          {
                            station.factory_count || 0
                          }
                        </div>
                      </div>
                    </Tooltip>

                  </Polygon>

                )
              )}

            </MapContainer>

          </div>

        </Card>


        <Card className="p-4">

          <div className="font-bold text-[#1a4d80] mb-4">
            المحطات
          </div>


          {loading ? (

            <div className="text-sm text-slate-500">
              جارٍ تحميل المحطات...
            </div>

          ) : stations.length === 0 ? (

            <div className="text-center py-10">

              <Building2
                size={38}
                className="mx-auto text-slate-300 mb-2"
              />

              <div className="font-bold">
                لا توجد محطات حتى الآن
              </div>

              <div className="text-xs text-slate-500 mt-1">
                اضغط «إنشاء محطة» للبدء
              </div>

            </div>

          ) : (

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">

              {stations.map(
                (station) => (

                  <div
                    key={station.id}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                  >

                    <div className="flex items-start justify-between gap-2">

                      <div>

                        <div className="font-bold text-slate-900">
                          {station.name}
                        </div>

                        <div className="text-xs text-slate-500 mt-1">
                          {station.code}
                          {' • '}
                          {station.city || '—'}
                        </div>

                      </div>

                      <Badge
                        className={
                          station.boundary?.length >= 3
                            ? 'bg-emerald-600'
                            : 'bg-amber-500'
                        }
                      >
                        {
                          station.boundary?.length >= 3
                            ? 'الحدود محددة'
                            : 'بانتظار تحديد الحدود'
                        }
                      </Badge>

                    </div>


                    <div className="grid grid-cols-2 gap-2 mt-4 text-xs">

                      <div className="rounded-lg bg-slate-50 p-2">
                        <Users size={13} className="inline ml-1"/>
                        المصانع:
                        {' '}
                        <b>
                          {station.factory_count || 0}
                        </b>
                      </div>

                      <div className="rounded-lg bg-slate-50 p-2">
                        <ShieldCheck size={13} className="inline ml-1"/>
                        المدراء:
                        {' '}
                        <b>
                          {station.manager_count || 0}
                        </b>
                      </div>

                    </div>


                    <div className="mt-3 text-[11px] text-slate-500">

                      <MapPin
                        size={12}
                        className="inline ml-1"
                      />

                      {station.boundary?.length >= 3
                        ? 'تم تحديد منطقة التغطية'
                        : 'يقوم مدير المحطة بتحديد حدود GPS'}
                    </div>

                  </div>

                )
              )}

            </div>

          )}

        </Card>

      </main>

    </div>
  );
}
